import React, { useState, useEffect } from 'react';
import { 
  X, 
  Upload, 
  Link as LinkIcon, 
  FileText, 
  Check, 
  AlertCircle, 
  Plus, 
  Edit2, 
  Trash2, 
  Download, 
  RefreshCw, 
  Film, 
  Tv, 
  Layers, 
  Sparkles, 
  Star,
  Search,
  Database,
  CheckCircle2,
  FolderPlus,
  Globe,
  Radio,
  ExternalLink,
  RotateCcw,
  Users,
  ShieldCheck
} from 'lucide-react';
import { 
  MediaItem, 
  EpisodeInfo, 
  CuratedCollection, 
  SyncSource, 
  SyncResult, 
  UserProfile,
  IptvSource 
} from '../types';
import { 
  parseAnimeCatJson,
  createCollectionFromJson,
  saveUserMediaItem,
  deleteUserMediaItem,
  exportUserCatalogToJson,
  subscribeToUserSyncSources,
  saveUserSyncSource,
  deleteUserSyncSource,
  syncUserSourceFromUrl,
  subscribeToUsers,
  cleanupDuplicateCollectionsAndCatalog,
  subscribeToIptvSources,
  DEFAULT_SYNC_SOURCES
} from '../lib/firebase';
import { fetchRemoteJson, normalizeRemoteUrl } from '../utils/fetchJson';
import { UserManagementTab } from './admin/UserManagementTab';
import { CollectionManagerTab } from './admin/CollectionManagerTab';
import { IptvManagerTab } from './admin/IptvManagerTab';

interface AdminDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  catalog: MediaItem[];
  collections: CuratedCollection[];
  currentUser?: UserProfile | null;
  onRefreshCatalog?: () => void | Promise<void>;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  isOpen,
  onClose,
  catalog,
  collections,
  currentUser,
  onRefreshCatalog
}) => {
  const userId = currentUser?.uid || '';

  // Check if current user is admin
  const isUserAdmin = Boolean(
    currentUser && (
      currentUser.role === 'admin' || 
      currentUser.username === 'admin' ||
      currentUser.email === 'admin@animecat.cat' || 
      currentUser.email === 'josep.g.frigole2@gmail.com'
    )
  );

  const [activeTab, setActiveTab] = useState<'import' | 'collections' | 'catalog' | 'sync' | 'iptv' | 'export' | 'users'>('import');

  // IPTV Sources State
  const [iptvSources, setIptvSources] = useState<IptvSource[]>([]);

  // Dashboard users state for admin tab
  const [dashboardUsers, setDashboardUsers] = useState<UserProfile[]>([]);

  // JSON Import States
  const [collectionNameInput, setCollectionNameInput] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: boolean; message: string } | null>(null);
  const [parsedPreview, setParsedPreview] = useState<{ seriesCount: number; moviesCount: number; sampleTitle?: string; episodesCount?: number } | null>(null);

  // Sync Sources States
  const [syncSources, setSyncSources] = useState<SyncSource[]>(DEFAULT_SYNC_SOURCES);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncingSourceId, setSyncingSourceId] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);
  const [isAddingSyncSource, setIsAddingSyncSource] = useState(false);
  const [editingSyncSource, setEditingSyncSource] = useState<SyncSource | null>(null);
  const [newSourceForm, setNewSourceForm] = useState({ name: '', url: '' });
  const [isCleaningDuplicates, setIsCleaningDuplicates] = useState(false);
  const [cleanDuplicatesMessage, setCleanDuplicatesMessage] = useState<string | null>(null);

  // Catalog Edit States
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [itemTypeToCreate, setItemTypeToCreate] = useState<'series' | 'movie'>('series');
  const [catalogFilter, setCatalogFilter] = useState<'all' | 'series' | 'movie'>('all');
  const [catalogCollectionFilter, setCatalogCollectionFilter] = useState<string>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [editingEpisode, setEditingEpisode] = useState<{ episode: EpisodeInfo; index: number } | null>(null);
  const [isAddingEpisode, setIsAddingEpisode] = useState(false);

  // Subscribe to Sync Sources & Users Overview
  useEffect(() => {
    if (!isOpen) return;
    const unsubSync = subscribeToUserSyncSources(userId, (sources) => {
      setSyncSources(sources);
    });

    const unsubIptv = subscribeToIptvSources(userId, (sources) => {
      setIptvSources(sources);
    });

    let unsubUsers = () => {};
    if (isUserAdmin) {
      unsubUsers = subscribeToUsers((uList) => {
        setDashboardUsers(uList);
      });
    }

    return () => {
      unsubSync();
      unsubIptv();
      unsubUsers();
    };
  }, [isOpen, userId, isUserAdmin]);

  if (!isOpen) return null;

  const pendingUsersCount = dashboardUsers.filter(u => u.status === 'pending').length;

  // Sample templates
  const loadUserSeriesSample = () => {
    const sample = {
      "generated_at": new Date().toISOString(),
      "series": null,
      "episodes": [],
      "movie": null,
      "series_list": [
        {
          "series": {
            "tmdb_id": 12609,
            "name": "Bola de Drac",
            "original_name": "ドラゴンボール",
            "summary": "L'argument se centra en en Son Goku, la Bulma, i la recerca de les llegendàries boles de drac, un total de set boles que en ser reunides donen lloc a l'aparició del drac Shenron que pot concedir qualsevol desig.",
            "poster": "https://image.tmdb.org/t/p/original/qevKflDRoskXnifUjz8QbS61lox.jpg",
            "backdrop": "https://image.tmdb.org/t/p/original/30L49n4Dhn7dzuGG50GV3ybMhC3.jpg",
            "genres": [
              "Animació",
              "Acció i Aventura",
              "Ciència-Ficció"
            ],
            "status": "Finalitzada",
            "first_air_date": "1986-02-26",
            "last_air_date": "1989-04-19",
            "number_of_seasons": 1,
            "number_of_episodes": 153
          },
          "episodes": [
            {
              "tmdb_id": 484769,
              "season": 1,
              "episode": 1,
              "title": "Bulma i Son Goku",
              "plot": "En Son Goku és un nen normal i corrent que viu al peu del pic de Paozu. Un bon dia topa amb la Bulma, una noia que busca boles de drac.",
              "air_date": "1986-02-26",
              "runtime": 25,
              "rating": 7.5,
              "poster": "https://image.tmdb.org/t/p/original/2RSrQ82HNbBBVM3omiHObQ0EvqI.jpg",
              "stream_url": "https://telegram.garco.ovh/stream/9984?token=3ieBt2A5AdQQkGXjSKoLrZR3pxK3Cq1uA454qHw0VlTsJljhOTjb6PQ4B7OJO063"
            }
          ]
        }
      ]
    };
    setCollectionNameInput('Bola de Drac Saga');
    setJsonText(JSON.stringify(sample, null, 2));
    validateAndPreviewJson(JSON.stringify(sample));
  };

  const loadUserMovieSample = () => {
    const sample = {
      "generated_at": new Date().toISOString(),
      "series": null,
      "episodes": [],
      "movie": null,
      "series_list": [],
      "movie_list": [
        {
          "tmdb_id": 15997,
          "title": "A la recerca de la vall encantada II: Aventura a la gran vall",
          "original_title": "The Land Before Time II: The Great Valley Adventure",
          "plot": "Els cinc petits amics continuen buscant aventures, encara que per això s'han d'allunyar de la seguretat de la vall.",
          "poster": "https://image.tmdb.org/t/p/original/8gWdRc32EBy99uGKOAfTC53kbgQ.jpg",
          "backdrop": "https://image.tmdb.org/t/p/original/8Db2ogq0e4UepMMxwqdv79YJMNq.jpg",
          "runtime": 73,
          "release_date": "1994-12-12",
          "rating": 6.3,
          "stream_url": "https://telegram.garco.ovh/stream/3409?token=3ieBt2A5AdQQkGXjSKoLrZR3pxK3Cq1uA454qHw0VlTsJljhOTjb6PQ4B7OJO063"
        }
      ]
    };
    setCollectionNameInput('Pel·lícules Vall Encantada');
    setJsonText(JSON.stringify(sample, null, 2));
    validateAndPreviewJson(JSON.stringify(sample));
  };

  const validateAndPreviewJson = (text: string) => {
    try {
      const { series, movies } = parseAnimeCatJson(text);
      const sCount = series.length;
      const mCount = movies.length;
      const sample = series[0]?.title || movies[0]?.title || '';
      const epCount = series.reduce((sum, s) => sum + (s.episodes?.length || 0), 0);

      if (sCount > 0 || mCount > 0) {
        setParsedPreview({
          seriesCount: sCount,
          moviesCount: mCount,
          episodesCount: epCount,
          sampleTitle: sample
        });
        return true;
      } else {
        setParsedPreview(null);
        return false;
      }
    } catch (e) {
      setParsedPreview(null);
      return false;
    }
  };

  // Handle File Upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!collectionNameInput.trim()) {
      const defaultName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      setCollectionNameInput(defaultName.charAt(0).toUpperCase() + defaultName.slice(1));
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonText(content);
      validateAndPreviewJson(content);
    };
    reader.readAsText(file);
  };

  // Handle Remote URL Fetch
  const handleFetchUrl = async (urlToFetch?: string) => {
    const target = (urlToFetch || urlInput).trim();
    if (!target) return;
    if (urlToFetch) {
      setUrlInput(urlToFetch);
    }
    setIsFetchingUrl(true);
    setImportStatus(null);
    try {
      const normalized = normalizeRemoteUrl(target);
      const { rawText } = await fetchRemoteJson(normalized);
      setJsonText(rawText);
      const isValid = validateAndPreviewJson(rawText);

      if (!collectionNameInput.trim()) {
        try {
          const urlObj = new URL(normalized);
          const pathname = urlObj.pathname.split('/').pop()?.replace('.json', '') || 'Col·lecció Remota';
          setCollectionNameInput(pathname.replace(/[-_]/g, ' '));
        } catch {
          setCollectionNameInput('Col·lecció Remota');
        }
      }

      if (isValid) {
        setImportStatus({ 
          success: true, 
          message: 'JSON obtingut i validat correctament des de la URL!' 
        });
      } else {
        setImportStatus({
          success: false,
          message: 'S\'ha descarregat el fitxer però no s\'han pogut identificar sèries o pel·lícules amb camps reconeguts.'
        });
      }
    } catch (err: any) {
      setImportStatus({ 
        success: false, 
        message: `Error al carregar la URL: ${err.message}. Verifica que l'enllaç sigui públic i retorni un JSON vàlid.` 
      });
    } finally {
      setIsFetchingUrl(false);
    }
  };

  // Commit Import as a New Collection to Firebase
  const handleCommitImport = async () => {
    if (!jsonText.trim()) return;
    const title = collectionNameInput.trim() || parsedPreview?.sampleTitle || `Col·lecció ${new Date().toLocaleDateString()}`;
    
    setIsImporting(true);
    try {
      const result = await createCollectionFromJson(userId, title, jsonText, urlInput.trim() || undefined);
      setImportStatus({
        success: true,
        message: `S'ha creat la col·lecció "${result.collection.title}" amb ${result.seriesCount} sèries i ${result.moviesCount} pel·lícules!`
      });
      setTimeout(() => {
        setImportStatus(null);
      }, 4000);
    } catch (err: any) {
      setImportStatus({
        success: false,
        message: `Error al crear la col·lecció: ${err.message}`
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Sync Single Remote Source
  const handleSyncSingleSource = async (source: SyncSource) => {
    setSyncingSourceId(source.id);
    setSyncResults(null);
    try {
      const res = await syncUserSourceFromUrl(userId, source);
      setSyncResults([res]);
    } catch (err: any) {
      setSyncResults([{
        sourceId: source.id,
        sourceName: source.name,
        success: false,
        seriesCount: 0,
        moviesCount: 0,
        episodesCount: 0,
        message: err.message || "Error en sincronitzar la font.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setSyncingSourceId(null);
    }
  };

  // Save sync source
  const handleSaveSyncSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSourceForm.name.trim() || !newSourceForm.url.trim()) return;

    const sourceToSave: SyncSource = editingSyncSource ? {
      ...editingSyncSource,
      name: newSourceForm.name.trim(),
      url: normalizeRemoteUrl(newSourceForm.url.trim()),
      userId: userId || 'guest'
    } : {
      id: `source-${Date.now()}`,
      name: newSourceForm.name.trim(),
      url: normalizeRemoteUrl(newSourceForm.url.trim()),
      userId: userId || 'guest',
      itemCount: 0,
      status: 'idle',
      createdAt: new Date().toISOString()
    };

    await saveUserSyncSource(userId, sourceToSave);
    setIsAddingSyncSource(false);
    setEditingSyncSource(null);
    setNewSourceForm({ name: '', url: '' });
  };

  // Delete sync source
  const handleDeleteSyncSource = async (sourceId: string) => {
    if (window.confirm("Segur que vols eliminar aquesta font de sincronització?")) {
      await deleteUserSyncSource(userId, sourceId);
    }
  };

  // Clean duplicate collections and catalog items
  const handleCleanDuplicates = async () => {
    if (!userId) return;
    setIsCleaningDuplicates(true);
    setCleanDuplicatesMessage(null);
    try {
      const res = await cleanupDuplicateCollectionsAndCatalog(userId);
      setCleanDuplicatesMessage(
        res.deletedCollections > 0 || res.deletedItems > 0
          ? `Neteja completada amb èxit: s'han eliminat ${res.deletedCollections} col·leccions repetides i ${res.deletedItems} títols redundants.`
          : "Tot al dia: No s'han trobat llistes ni títols duplicats al teu compte."
      );
      setTimeout(() => setCleanDuplicatesMessage(null), 5000);
    } catch (err: any) {
      setCleanDuplicatesMessage(`Error en netejar duplicats: ${err.message}`);
    } finally {
      setIsCleaningDuplicates(false);
    }
  };

  // Export User Catalog JSON
  const handleExportJson = async () => {
    try {
      const fullData = await exportUserCatalogToJson(userId);
      const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `garcollections_cataleg_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting catalog:', err);
    }
  };

  // Filter Catalog
  const filteredCatalog = catalog.filter((item) => {
    const matchesType = catalogFilter === 'all' || item.type === catalogFilter;
    const matchesCollection = catalogCollectionFilter === 'all' || item.collectionId === catalogCollectionFilter;
    const matchesSearch = !catalogSearch || 
      item.title.toLowerCase().includes(catalogSearch.toLowerCase()) ||
      item.tmdbId?.toString().includes(catalogSearch);
    return matchesType && matchesCollection && matchesSearch;
  });

  // Save item changes
  const handleSaveMediaItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    await saveUserMediaItem(userId, editingItem);
    setEditingItem(null);
    setIsCreatingNew(false);
  };

  // Delete Media Item
  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm("Segur que vols eliminar aquest element del teu catàleg?")) {
      await deleteUserMediaItem(userId, itemId);
    }
  };

  return (
    <div 
      id="admin-dashboard-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/85 backdrop-blur-md animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-5xl h-[90vh] bg-[#0c1220] border border-sky-500/30 rounded-3xl shadow-2xl shadow-black flex flex-col overflow-hidden text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header */}
        <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between bg-[#0f1628] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-cyan-400 flex items-center justify-center text-white font-bold shadow-lg shadow-sky-500/25">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-display font-black text-lg sm:text-xl text-white">
                  Panell de Gestió i Col·leccions
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500/20 text-sky-300 border border-sky-500/30">
                  DB: garcollections2
                </span>
                {isUserAdmin && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    Admin
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Pujada de fitxers JSON, gestió de col·leccions personals, edició de sèries i pel·lícules.
              </p>
            </div>
          </div>

          <button
            id="close-admin-button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Workspace Body */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          
          {/* Sidebar Navigation */}
          <div className="w-full md:w-60 bg-[#0d1424] border-r border-white/10 p-3 space-y-1 shrink-0 flex md:flex-col overflow-x-auto">
            
            <button
              id="admin-tab-import"
              onClick={() => setActiveTab('import')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'import' 
                  ? 'bg-sky-500 text-black shadow-md font-bold' 
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Upload className="w-4 h-4 shrink-0" />
              <span>Pujar JSON / Nova Col·lecció</span>
            </button>

            <button
              id="admin-tab-collections"
              onClick={() => setActiveTab('collections')}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'collections' 
                  ? 'bg-sky-500 text-black shadow-md font-bold' 
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2.5 truncate">
                <Layers className="w-4 h-4 shrink-0" />
                <span className="truncate">Col·leccions ({collections.length})</span>
              </div>
            </button>

            <button
              id="admin-tab-catalog"
              onClick={() => setActiveTab('catalog')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'catalog' 
                  ? 'bg-sky-500 text-black shadow-md font-bold' 
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Tv className="w-4 h-4 shrink-0" />
              <span>Catàleg de Títols ({catalog.length})</span>
            </button>

            <button
              id="admin-tab-sync"
              onClick={() => setActiveTab('sync')}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'sync' 
                  ? 'bg-sky-500 text-black shadow-md font-bold' 
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2.5 truncate">
                <Radio className="w-4 h-4 shrink-0 text-cyan-400" />
                <span className="truncate">Fonts Remotes ({syncSources.length})</span>
              </div>
            </button>

            <button
              id="admin-tab-iptv"
              onClick={() => setActiveTab('iptv')}
              className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'iptv' 
                  ? 'bg-red-600 text-white shadow-md font-bold' 
                  : 'text-red-300 hover:bg-red-500/10 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2.5 truncate">
                <Tv className="w-4 h-4 shrink-0 text-red-400" />
                <span className="truncate">Fonts IPTV / Xtream ({iptvSources.length})</span>
              </div>
            </button>

            <button
              id="admin-tab-export"
              onClick={() => setActiveTab('export')}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                activeTab === 'export' 
                  ? 'bg-sky-500 text-black shadow-md font-bold' 
                  : 'text-slate-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Download className="w-4 h-4 shrink-0" />
              <span>Exportar Dades</span>
            </button>

            {/* ONLY for admin role */}
            {isUserAdmin && (
              <div className="pt-2 border-t border-white/10 mt-2">
                <button
                  id="admin-tab-users"
                  onClick={() => setActiveTab('users')}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold w-full text-left transition-all ${
                    activeTab === 'users' 
                      ? 'bg-purple-600 text-white shadow-md font-bold' 
                      : 'text-purple-300 hover:bg-purple-500/15 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <Users className="w-4 h-4 shrink-0 text-purple-400" />
                    <span className="truncate">Gestió Usuaris ({dashboardUsers.length})</span>
                  </div>
                  {pendingUsersCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black bg-purple-400 text-black animate-pulse">
                      {pendingUsersCount}
                    </span>
                  )}
                </button>
              </div>
            )}

          </div>

          {/* Main Tab Content Area */}
          <div className="flex-1 p-4 sm:p-6 overflow-y-auto bg-[#090e1a]">
            
            {/* TAB 1: JSON IMPORTER -> Creates Collection */}
            {activeTab === 'import' && (
              <div className="space-y-6 max-w-3xl">
                
                <div>
                  <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                    <Upload className="w-5 h-5 text-sky-400" />
                    Pujar Fitxer JSON per Crear una Col·lecció
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Cada fitxer JSON que pugis esdevindrà una col·lecció independent visible al menú superior.
                  </p>
                </div>

                {/* Collection Name Input */}
                <div className="p-4 rounded-2xl bg-[#0f1629] border border-sky-500/30 space-y-2 shadow-inner">
                  <label className="block text-xs font-bold text-sky-300">
                    1. Nom de la Col·lecció
                  </label>
                  <input
                    type="text"
                    value={collectionNameInput}
                    onChange={(e) => setCollectionNameInput(e.target.value)}
                    placeholder="Ex: Animes d'Aventura, Pel·lícules Clàssiques, Saga Bola de Drac..."
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-900 border border-white/15 text-white text-sm focus:border-sky-400 focus:outline-none placeholder-slate-500"
                  />
                  <p className="text-[11px] text-slate-400">
                    Aquest serà el nom que apareixerà al desplegable de col·leccions del menú superior.
                  </p>
                </div>

                {/* Preloaded Template Quick Buttons */}
                <div className="p-4 rounded-2xl bg-[#0d1424] border border-white/5 space-y-3">
                  <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-sky-400" />
                    Plantilles de Prova Ràpides
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={loadUserSeriesSample}
                      className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 hover:border-sky-500/40 text-xs text-sky-300 font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <Tv className="w-3.5 h-3.5" />
                      Plantilla Sèrie: Bola de Drac
                    </button>
                    <button
                      onClick={loadUserMovieSample}
                      className="px-3 py-1.5 rounded-xl bg-white/5 hover:bg-cyan-500/20 border border-white/10 hover:border-cyan-500/40 text-xs text-cyan-300 font-semibold transition-colors flex items-center gap-1.5"
                    >
                      <Film className="w-3.5 h-3.5" />
                      Plantilla Pel·lícula: Vall Encantada
                    </button>
                  </div>
                </div>

                {/* URL Loader */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-300">
                    2. Opcional: Carregar des d'una URL pública (JSON)
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <LinkIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="url"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleFetchUrl();
                          }
                        }}
                        placeholder="https://f003.backblazeb2.com/file/garcomedia/collections/animecat-movies.json"
                        className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={() => handleFetchUrl()}
                      disabled={isFetchingUrl || !urlInput.trim()}
                      className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold transition-colors shrink-0 disabled:opacity-40"
                    >
                      {isFetchingUrl ? 'Carregant...' : 'Descarrega'}
                    </button>
                  </div>
                </div>

                {/* File Drag and Drop */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-300">
                    O bé puja un fitxer .json des del teu dispositiu
                  </label>
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-white/15 hover:border-sky-500/50 rounded-2xl cursor-pointer bg-slate-900/50 hover:bg-slate-900 transition-colors">
                    <Upload className="w-8 h-8 text-sky-400 mb-2" />
                    <span className="text-xs font-semibold text-slate-200">
                      Fes clic o arrossega el fitxer JSON aquí
                    </span>
                    <span className="text-[11px] text-slate-400 mt-1">
                      S'accepta estructura amb series_list, movie_list, o directes.
                    </span>
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* JSON Textarea */}
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-slate-300">
                    Contingut JSON (Pots enganxar-lo o editar-lo directament)
                  </label>
                  <textarea
                    rows={8}
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      validateAndPreviewJson(e.target.value);
                    }}
                    placeholder='{"series_list": [...], "movie_list": [...]}'
                    className="w-full p-3 rounded-2xl bg-slate-900/90 border border-white/10 text-slate-200 font-mono text-xs focus:border-sky-500 focus:outline-none"
                  />
                </div>

                {/* Preview Box */}
                {parsedPreview && (
                  <div className="p-4 rounded-2xl bg-sky-500/10 border border-sky-500/30 space-y-2 animate-in fade-in">
                    <div className="flex items-center gap-2 text-sky-300 font-semibold text-xs">
                      <CheckCircle2 className="w-4 h-4 text-sky-400" />
                      <span>Validació correcta: Es crearà la col·lecció</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="p-2 bg-slate-900/60 rounded-xl border border-white/5">
                        <div className="text-slate-400 text-[10px]">Sèries</div>
                        <div className="text-base font-bold text-white">{parsedPreview.seriesCount}</div>
                      </div>
                      <div className="p-2 bg-slate-900/60 rounded-xl border border-white/5">
                        <div className="text-slate-400 text-[10px]">Pel·lícules</div>
                        <div className="text-base font-bold text-white">{parsedPreview.moviesCount}</div>
                      </div>
                      <div className="p-2 bg-slate-900/60 rounded-xl border border-white/5">
                        <div className="text-slate-400 text-[10px]">Episodis totals</div>
                        <div className="text-base font-bold text-white">{parsedPreview.episodesCount}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Import Status Alert */}
                {importStatus && (
                  <div className={`p-4 rounded-xl text-xs flex items-center gap-2 border animate-in fade-in ${
                    importStatus.success 
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' 
                      : 'bg-red-500/15 border-red-500/40 text-red-300'
                  }`}>
                    {importStatus.success ? <Check className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                    <span>{importStatus.message}</span>
                  </div>
                )}

                {/* Commit Import Button */}
                <button
                  onClick={handleCommitImport}
                  disabled={!parsedPreview || isImporting}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-sm shadow-xl shadow-sky-500/20 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>
                    {isImporting ? 'Creant Col·lecció...' : `Crear Col·lecció "${collectionNameInput || 'Nova'}" i Afegir al Catàleg`}
                  </span>
                </button>

              </div>
            )}

            {/* TAB 2: COLLECTIONS MANAGER */}
            {activeTab === 'collections' && (
              <CollectionManagerTab 
                collections={collections}
                catalog={catalog}
                currentUser={currentUser}
                onOpenImport={() => setActiveTab('import')}
              />
            )}

            {/* TAB 3: CATALOG & EPISODES MANAGER */}
            {activeTab === 'catalog' && (
              <div className="space-y-5 max-w-5xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                      <Tv className="w-5 h-5 text-sky-400" />
                      Gestió del Teu Catàleg
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Filtra per col·lecció o tipus, edita metadades o afegeix nous títols.
                    </p>
                  </div>
                </div>

                {/* Filters Bar */}
                <div className="flex flex-wrap gap-2 items-center justify-between bg-[#0f1629] p-3 rounded-2xl border border-white/5">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Collection filter */}
                    <select
                      value={catalogCollectionFilter}
                      onChange={(e) => setCatalogCollectionFilter(e.target.value)}
                      className="px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:outline-none"
                    >
                      <option value="all">Totes les col·leccions</option>
                      {collections.map(c => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>

                    {/* Type filter */}
                    <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-white/10 text-xs">
                      <button
                        onClick={() => setCatalogFilter('all')}
                        className={`px-2.5 py-1 rounded-lg ${catalogFilter === 'all' ? 'bg-sky-500 text-black font-bold' : 'text-slate-400'}`}
                      >
                        Tots ({catalog.length})
                      </button>
                      <button
                        onClick={() => setCatalogFilter('series')}
                        className={`px-2.5 py-1 rounded-lg ${catalogFilter === 'series' ? 'bg-sky-500 text-black font-bold' : 'text-slate-400'}`}
                      >
                        Sèries
                      </button>
                      <button
                        onClick={() => setCatalogFilter('movie')}
                        className={`px-2.5 py-1 rounded-lg ${catalogFilter === 'movie' ? 'bg-sky-500 text-black font-bold' : 'text-slate-400'}`}
                      >
                        Pel·lícules
                      </button>
                    </div>
                  </div>

                  {/* Search bar */}
                  <div className="relative w-full sm:w-64">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                      placeholder="Cerca per títol o TMDB ID..."
                      className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white text-xs focus:border-sky-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Edit Form Modal if editingItem is active */}
                {editingItem && (
                  <div className="p-5 rounded-2xl bg-[#0f1629] border-2 border-sky-500/50 space-y-4 animate-in fade-in">
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <h4 className="font-bold text-sm text-white flex items-center gap-2">
                        <Edit2 className="w-4 h-4 text-sky-400" />
                        Editar Títol: {editingItem.title}
                      </h4>
                      <button
                        onClick={() => setEditingItem(null)}
                        className="p-1 rounded-lg text-slate-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <form onSubmit={handleSaveMediaItem} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                        <div className="space-y-1">
                          <label className="text-slate-300 font-semibold">Títol en Català</label>
                          <input
                            type="text"
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                            required
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-slate-300 font-semibold">Títol Original</label>
                          <input
                            type="text"
                            value={editingItem.originalTitle || ''}
                            onChange={(e) => setEditingItem({ ...editingItem, originalTitle: e.target.value })}
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-slate-300 font-semibold">Col·lecció Pertanyent</label>
                          <select
                            value={editingItem.collectionId || ''}
                            onChange={(e) => {
                              const found = collections.find(c => c.id === e.target.value);
                              setEditingItem({
                                ...editingItem,
                                collectionId: e.target.value,
                                collectionName: found?.title || ''
                              });
                            }}
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                          >
                            <option value="">Sense col·lecció</option>
                            {collections.map(c => (
                              <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1">
                          <label className="text-slate-300 font-semibold">URL Pòster</label>
                          <input
                            type="url"
                            value={editingItem.poster}
                            onChange={(e) => setEditingItem({ ...editingItem, poster: e.target.value })}
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-slate-300 font-semibold">Puntuació (0 - 10)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={editingItem.rating || 8.0}
                            onChange={(e) => setEditingItem({ ...editingItem, rating: parseFloat(e.target.value) })}
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                          />
                        </div>

                        {editingItem.type === 'movie' && (
                          <div className="space-y-1">
                            <label className="text-slate-300 font-semibold">URL de Streaming</label>
                            <input
                              type="url"
                              value={editingItem.streamUrl || ''}
                              onChange={(e) => setEditingItem({ ...editingItem, streamUrl: e.target.value })}
                              className="w-full px-3 py-1.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                            />
                          </div>
                        )}
                      </div>

                      <div className="space-y-1 text-xs">
                        <label className="text-slate-300 font-semibold">Sinopsi</label>
                        <textarea
                          rows={3}
                          value={editingItem.summary || ''}
                          onChange={(e) => setEditingItem({ ...editingItem, summary: e.target.value })}
                          className="w-full p-2.5 rounded-xl bg-slate-900 border border-white/10 text-white"
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setEditingItem(null)}
                          className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-semibold"
                        >
                          Cancel·lar
                        </button>
                        <button
                          type="submit"
                          className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold"
                        >
                          Desar Canvis
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Items List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filteredCatalog.map((item) => (
                    <div 
                      key={item.id}
                      className="p-3 rounded-2xl bg-[#0d1424] border border-white/5 hover:border-sky-500/40 transition-all flex flex-col justify-between space-y-2 text-xs"
                    >
                      <div className="flex gap-3">
                        <img
                          src={item.poster}
                          alt={item.title}
                          className="w-14 h-20 object-cover rounded-xl border border-white/10 shrink-0"
                          referrerPolicy="no-referrer"
                        />
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-sky-500/20 text-sky-300">
                              {item.type === 'series' ? 'Sèrie' : 'Pel·lícula'}
                            </span>
                            {item.collectionName && (
                              <span className="text-[9px] text-slate-400 truncate block">
                                {item.collectionName}
                              </span>
                            )}
                          </div>
                          <h4 className="font-bold text-white truncate">{item.title}</h4>
                          <p className="text-[10px] text-slate-400">
                            {item.type === 'series' ? `${item.episodes?.length || 0} eps` : `${item.runtime || 90} min`}
                          </p>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-white/5 flex items-center justify-between">
                        <span className="text-[10px] text-slate-400">TMDB: {item.tmdbId}</span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingItem(item)}
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-sky-500 hover:text-black text-slate-300 transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500 hover:text-white text-slate-400 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {filteredCatalog.length === 0 && (
                    <div className="col-span-full py-12 text-center text-slate-400">
                      <p className="text-sm font-semibold">No s'han trobat elements amb els filtres actuals.</p>
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* TAB 4: REMOTE SYNC SOURCES */}
            {activeTab === 'sync' && (
              <div className="space-y-6 max-w-4xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                      <Radio className="w-5 h-5 text-cyan-400" />
                      Fonts Remotes de Sincronització
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      Afegeix enllaços a fitxers JSON a internet per mantenir el teu catàleg sempre actualitzat.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCleanDuplicates}
                      disabled={isCleaningDuplicates}
                      className="px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-slate-200 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50 border border-white/10"
                      title="Elimina qualsevol col·lecció o títol duplicat provinent de sincronitzacions anteriors"
                    >
                      <Sparkles className={`w-4 h-4 text-amber-400 ${isCleaningDuplicates ? 'animate-spin' : ''}`} />
                      <span>{isCleaningDuplicates ? 'Netejant duplicats...' : 'Netejar Duplicats'}</span>
                    </button>
                    <button
                      onClick={() => setIsAddingSyncSource(true)}
                      className="px-3.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-black text-xs font-bold flex items-center gap-1.5"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Nova Font</span>
                    </button>
                  </div>
                </div>

                {/* Clean Duplicates Message */}
                {cleanDuplicatesMessage && (
                  <div className="p-3 rounded-xl text-xs bg-sky-500/15 border border-sky-500/30 text-sky-200 flex items-center gap-2 animate-in fade-in">
                    <CheckCircle2 className="w-4 h-4 text-sky-400 shrink-0" />
                    <span>{cleanDuplicatesMessage}</span>
                  </div>
                )}

                {/* Add/Edit Sync Source Modal */}
                {isAddingSyncSource && (
                  <form onSubmit={handleSaveSyncSource} className="p-4 rounded-2xl bg-[#0f1629] border border-sky-500/40 space-y-3">
                    <h4 className="text-xs font-bold text-white">Afegir Nova Font JSON</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <input
                        type="text"
                        placeholder="Nom descriptiu (ex: Sèries GarcoMedia)"
                        value={newSourceForm.name}
                        onChange={(e) => setNewSourceForm({ ...newSourceForm, name: e.target.value })}
                        className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white"
                        required
                      />
                      <input
                        type="url"
                        placeholder="https://.../cataleg.json"
                        value={newSourceForm.url}
                        onChange={(e) => setNewSourceForm({ ...newSourceForm, url: e.target.value })}
                        className="px-3 py-2 rounded-xl bg-slate-900 border border-white/10 text-white"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setIsAddingSyncSource(false)}
                        className="px-3 py-1.5 rounded-xl bg-white/5 text-slate-300 text-xs"
                      >
                        Cancel·lar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-1.5 rounded-xl bg-sky-500 text-black font-bold text-xs"
                      >
                        Desar Font
                      </button>
                    </div>
                  </form>
                )}

                {/* Sync Results Banner */}
                {syncResults && (
                  <div className="space-y-2">
                    {syncResults.map((r, i) => (
                      <div key={i} className={`p-3 rounded-xl text-xs flex items-center justify-between border ${
                        r.success ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-red-500/15 border-red-500/40 text-red-300'
                      }`}>
                        <span>{r.sourceName}: {r.message}</span>
                        {r.success && <span>{r.seriesCount} sèries, {r.moviesCount} pel·lis</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Sources List */}
                <div className="space-y-3">
                  {syncSources.map((source) => (
                    <div 
                      key={source.id}
                      className="p-4 rounded-2xl bg-[#0d1424] border border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-white text-sm truncate">{source.name}</h4>
                          <span className="px-2 py-0.5 rounded text-[9px] font-mono bg-white/5 text-slate-300">
                            {source.itemCount || 0} títols
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 font-mono truncate">{source.url}</p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleSyncSingleSource(source)}
                          disabled={syncingSourceId === source.id}
                          className="px-3 py-1.5 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 font-semibold border border-sky-500/30 flex items-center gap-1.5"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${syncingSourceId === source.id ? 'animate-spin' : ''}`} />
                          <span>Sincronitzar</span>
                        </button>
                        <button
                          onClick={() => handleDeleteSyncSource(source.id)}
                          className="p-1.5 rounded-xl bg-white/5 hover:bg-red-500 hover:text-white text-slate-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 4.5: IPTV SOURCES */}
            {activeTab === 'iptv' && (
              <IptvManagerTab 
                sources={iptvSources} 
                userId={userId} 
              />
            )}

            {/* TAB 5: EXPORT */}
            {activeTab === 'export' && (
              <div className="space-y-6 max-w-2xl">
                <div>
                  <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                    <Download className="w-5 h-5 text-sky-400" />
                    Exportar el Teu Catàleg i Col·leccions
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Descarrega un fitxer JSON complet amb totes les teves col·leccions, sèries, pel·lícules i enllaços de streaming.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/5 space-y-4 text-xs">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-white text-sm">Còpia de Seguretat JSON</h4>
                      <p className="text-slate-400 mt-0.5">{collections.length} col·leccions i {catalog.length} títols registrats.</p>
                    </div>
                    <button
                      onClick={handleExportJson}
                      className="px-4 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-black font-bold flex items-center gap-2 shadow-lg"
                    >
                      <Download className="w-4 h-4" />
                      <span>Descarregar Fitxer JSON</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 6: USERS (ADMIN ONLY) */}
            {activeTab === 'users' && isUserAdmin && (
              <UserManagementTab />
            )}

          </div>

        </div>

      </div>
    </div>
  );
};
